import {
  get, post, put, destroy,
} from '../utils/request';
import { credentials, setUser } from './user';

export const handleCreateNewSubmission = (params, user) => async (dispatch) => {
  try {
    const response = await post('/submission', params, dispatch, credentials(user));

    const body = await response.json();
    const { submissionCount } = body;

    // Le compte remonte dans l'utilisateur : sans ça, « Bâtissez votre première
    // soumission » restait décochée jusqu'au prochain chargement complet de la page,
    // la validation de jeton ne se rejouant pas d'un écran à l'autre.
    if (submissionCount !== undefined) dispatch(setUser({ submissionCount }));

    return true;
  } catch (error) {
    return false;
  }
};

export const handleDeleteSubmission = (user, idsToDelete, submissionId) => async (dispatch) => {
  try {
    const params = {
      ids: idsToDelete,
    };

    const response = await post('/delete_submission', params, dispatch, credentials(user));

    const body = await response.json();
    const { submissionCount } = body;

    if (submissionCount !== undefined) dispatch(setUser({ submissionCount }));

    if (idsToDelete.includes(submissionId) || submissionCount === 0) {
      dispatch(resetActiveSubmission());
    }

    return true;
  } catch (error) {
    return false;
  }
};

export const handleDeleteActiveSubmissionItems = (params, user) => async (dispatch) => {
  try {
    const response = await post('/delete_submission_items', params, dispatch, credentials(user));

    const body = await response.json();

    if (body) await dispatch(setActiveSubmissionAfterItemDeleteOrCreate(body));

    return true;
  } catch (error) {
    return false;
  }
};

// Refus du serveur sur un item (422) : on remonte SON message, pas un « une erreur est
// survenue » qui n'aide personne. Le corps porte `errors` (liste) et `error` (phrase).
// Les appelants reconnaissent l'échec à la présence de `.error` — un retour sans `.error`
// reste un succès, comme avant.
const itemFailure = (body) => ({
  error: (Array.isArray(body && body.errors) && body.errors.length)
    ? body.errors.join(' · ')
    : ((body && body.error) || 'Une erreur est survenue'),
});

export const handleUpdateActiveSubmissionItem = (params, user, id) => async (dispatch) => {
  try {
    const response = await put(`/submission_item/${id}`, params, dispatch, credentials(user));

    const body = await response.json();
    // Sans ce garde-fou, la liste inchangée renvoyée par un refus était réinjectée telle
    // quelle dans le store et l'écran annonçait « Enregistré » : la modification n'existait
    // nulle part. Le corps d'erreur ne porte pas de liste — on n'écrase rien.
    if (!response.ok) return itemFailure(body);

    if (body) await dispatch(setActiveSubmissionAfterItemDeleteOrCreate(body));
    // Renvoie le CORPS (comme handleAddNewItemToSubmission) plutôt qu'un simple `true` :
    // il porte `duplicate`, le signalement du serveur quand un renommage vient de rendre
    // cette ligne identique à une autre de la même section. Les appelants testent la
    // vérité ou passent par failureOf — un corps sans `error` reste un succès.
    return body || true;
  } catch (error) {
    return { error: 'Une erreur est survenue' };
  }
};

// Additionne deux lignes devenues identiques après un renommage — le geste que propose le
// signalement `duplicate` de la mise à jour. La réponse porte la liste à jour et la ligne
// CONSERVÉE (`merged_item_id`), comme la fusion à l'ajout.
export const handleMergeDuplicateItems = (params, user) => async (dispatch) => {
  try {
    const response = await post('/merge_submission_items', params, dispatch, credentials(user));

    const body = await response.json();
    if (!response.ok) return itemFailure(body);

    if (body) await dispatch(setActiveSubmissionAfterItemDeleteOrCreate(body));
    return body || true;
  } catch (error) {
    return { error: 'Une erreur est survenue' };
  }
};

export const handleAddNewItemToSubmission = (params, user) => async (dispatch) => {
  try {
    const response = await post('/submission_item', params, dispatch, credentials(user));

    const body = await response.json();
    if (!response.ok) return itemFailure(body);

    if (body) dispatch(setActiveSubmissionAfterItemDeleteOrCreate(body));

    // Renvoie le corps (qui porte submissionItemList à jour) plutôt qu'un simple booléen :
    // les appelants qui testent la vérité fonctionnent toujours, et la création d'assemblage
    // peut y RETROUVER l'item parent fraîchement créé (props pas encore re-rendus).
    return body || true;
  } catch (error) {
    return { error: 'Une erreur est survenue' };
  }
};

export const handleSetActiveSubmission = (user, submissionId) => async (dispatch) => {
  try {
    if (submissionId !== '-1') {
      const response = await get(`/submission/${submissionId}`, dispatch, credentials(user));
      const body = await response.json();

      if (body) await dispatch(setActiveSubmission(body));
    } else {
      dispatch(resetActiveSubmission());
    }

    return true;
  } catch (error) {
    return false;
  }
};

// COPIE SANS PRIX → URL blob locale.
//
// Route DISTINCTE du téléchargement normal : celui-là sert le contrat signé figé dès
// qu'il existe, et cette garantie ne doit pas être trouée par un paramètre. Ici on ne
// peut obtenir QUE la variante sans montants, et elle n'écrit rien en base.
export const fetchSubmissionPdfWithoutPrices = (submissionId, withProviders, user) => async () => {
  try {
    const response = await get(
      `/submission/${submissionId}/pdf_without_prices?providers=${withProviders ? 1 : 0}`,
      null,
      credentials(user),
    );
    if (!response.ok) return null;
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (e) {
    return null;
  }
};

export const setActiveSubmissionSections = (sections) => ({
  type: 'SET_ACTIVE_SUBMISSION_SECTIONS',
  sections,
});

export const handleCreateSection = (params, user) => async (dispatch) => {
  try {
    const response = await post('/submission_section', params, dispatch, credentials(user));
    const body = await response.json();
    if (body && body.sections) dispatch(setActiveSubmissionSections(body.sections));
    return body && body.sections ? body.sections : null;
  } catch (error) {
    return null;
  }
};

export const handleUpdateSection = (id, params, user) => async (dispatch) => {
  try {
    const response = await put(`/submission_section/${id}`, params, dispatch, credentials(user));
    const body = await response.json();
    if (body && body.sections) dispatch(setActiveSubmissionSections(body.sections));
    return true;
  } catch (error) {
    return false;
  }
};

export const handleDeleteSection = (id, params, user) => async (dispatch) => {
  try {
    const response = await destroy(`/submission_section/${id}?submission_id=${params.submission_id}`, dispatch, credentials(user));
    const body = await response.json();
    if (body && body.sections) dispatch(setActiveSubmissionSections(body.sections));
    return true;
  } catch (error) {
    return false;
  }
};

export const handleReorderSections = (params, user) => async (dispatch) => {
  try {
    const response = await post('/order_submission_sections', params, dispatch, credentials(user));
    const body = await response.json();
    if (body && body.sections) dispatch(setActiveSubmissionSections(body.sections));
    return true;
  } catch (error) {
    return false;
  }
};

// ===== SOUS-SECTIONS (2e niveau dans une section, Phase 2.2) =====
// L'API renvoie l'arbre complet sections → sous-sections ; on redispatch
// setActiveSubmissionSections (même canal que les sections).
export const handleCreateSubSection = (params, user) => async (dispatch) => {
  try {
    const response = await post('/submission_sub_section', params, dispatch, credentials(user));
    const body = await response.json();
    if (body && body.sections) dispatch(setActiveSubmissionSections(body.sections));
    return body && body.sections ? body.sections : null;
  } catch (error) {
    return null;
  }
};

export const handleUpdateSubSection = (id, params, user) => async (dispatch) => {
  try {
    const response = await put(`/submission_sub_section/${id}`, params, dispatch, credentials(user));
    const body = await response.json();
    if (body && body.sections) dispatch(setActiveSubmissionSections(body.sections));
    return true;
  } catch (error) {
    return false;
  }
};

export const handleDeleteSubSection = (id, params, user) => async (dispatch) => {
  try {
    const response = await destroy(`/submission_sub_section/${id}?submission_id=${params.submission_id}`, dispatch, credentials(user));
    const body = await response.json();
    if (body && body.sections) dispatch(setActiveSubmissionSections(body.sections));
    return true;
  } catch (error) {
    return false;
  }
};

export const handleReorderSubSections = (params, user) => async (dispatch) => {
  try {
    const response = await post('/order_submission_sub_sections', params, dispatch, credentials(user));
    const body = await response.json();
    if (body && body.sections) dispatch(setActiveSubmissionSections(body.sections));
    return true;
  } catch (error) {
    return false;
  }
};

// Réordonnancement d'items en mode SECTION (glisser-déposer, Phase 1.5) :
// POST /reorder_submission_items renvoie le submissionItemList à jour (trié par
// numéro d'ordre). Seul chemin de réordonnancement depuis le retrait de
// /order_submission_items (P3-4).
export const handleReorderItems = (params, user) => async (dispatch) => {
  try {
    const response = await post('/reorder_submission_items', params, dispatch, credentials(user));
    const body = await response.json();
    if (body) dispatch(setActiveSubmissionItemAfterPriorityChange(body));
    return true;
  } catch (error) {
    return false;
  }
};

export const handleSetActiveSubmissionClientInformations = (user, params, submissionId) => async (dispatch) => {
  try {
    const response = await put(`/submission/${submissionId}`, params, dispatch, credentials(user));

    const body = await response.json();
    const { data } = body;

    if (data) await dispatch(setActiveSubmissionAfterClientInfoUpdate(data));

    return true;
  } catch (error) {
    return false;
  }
};

export const setActiveSubmissionAfterClientInfoUpdate = (clientInfo) => ({
  type: 'SET_ACTIVE_SUBMISSION_AFTER_CLIENT_INFO_UPDATE',
  clientInfo,
});

export const setActiveSubmissionAfterItemDeleteOrCreate = (submission) => ({
  type: 'SET_ACTIVE_SUBMISSION_AFTER_ITEM_DELETE_OR_CREATE',
  submission,
});

// Patch local optimiste d'un item de la soumission active (édition en place).
export const optimisticPatchItem = (id, patch) => ({
  type: 'OPTIMISTIC_PATCH_ITEM',
  id,
  patch,
});

// Remplace la liste d'items localement (suppression optimiste / retour en arrière).
export const optimisticSetItems = (items) => ({
  type: 'OPTIMISTIC_SET_ITEMS',
  items,
});

export const setActiveSubmissionItemAfterPriorityChange = (submission) => ({
  type: 'SET_ACTIVE_SUBMISSION_ITEMS_AFTER_PRIORITY_CHANGE',
  submission,
});

export const setActiveSubmission = (submission) => ({
  type: 'SET_ACTIVE_SUBMISSION',
  submission,
});

export const resetActiveSubmission = () => ({
  type: 'RESET_ACTIVE_SUBMISSION',
});

